import { ExternalLink, X } from 'lucide-react'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useAddPOTracking,
  useDeletePOTracking,
  usePOTracking,
  type POTrackingEntry,
  type TrackingCarrier,
} from '@/hooks/usePOTracking'
import { usePO } from '@/hooks/usePOs'
import { ApiError } from '@/lib/api/rawFetch'
import { fmtDate } from '@/lib/dates'
import { useUIStore } from '@/store/uiStore'

// "Other" records a shipment on a carrier we have no tracking-URL template
// for; the backend returns no url for it and the row renders as plain text.
const CARRIERS: TrackingCarrier[] = ['UPS', 'DHL', 'FedEx', 'USPS', 'Other']

// Tracking numbers are free-form across carriers — we only trim and require
// non-empty rather than inventing a format regex that would reject valid ones.
const trackingSchema = z.object({
  carrier: z.enum(['UPS', 'DHL', 'FedEx', 'USPS', 'Other']),
  tracking_number: z.string().trim().min(1, 'Tracking number is required'),
  // Optional — a carrier often has no ETA on the day the label is created.
  // Held as the raw 'YYYY-MM-DD' from <input type="date">, which is exactly
  // what the API wants, so there's no parsing (and no timezone) in between.
  expected_delivery: z.string(),
})

type TrackingValues = z.infer<typeof trackingSchema>

const EMPTY: TrackingValues = {
  carrier: 'UPS',
  tracking_number: '',
  expected_delivery: '',
}

function TrackingRow({
  entry,
  poId,
}: {
  entry: POTrackingEntry
  poId: string | null
}) {
  const remove = useDeletePOTracking(poId)

  function handleRemove() {
    if (entry.tracking_id == null) return
    remove.mutate(entry.tracking_id, {
      onSuccess: () => toast.success(`Removed ${entry.tracking_number}`),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Failed to remove tracking'),
    })
  }

  return (
    <li className="flex items-center gap-2 border-b border-[var(--line-warm)] py-2 last:border-b-0">
      <span className="w-12 shrink-0 text-xs font-medium text-[var(--text-primary)]">
        {entry.carrier}
      </span>
      {entry.url ? (
        <a
          href={entry.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-w-0 items-center gap-1 text-xs tabular-nums text-[var(--brand)] hover:underline"
        >
          <span className="truncate">{entry.tracking_number}</span>
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
      ) : (
        <span className="min-w-0 truncate text-xs tabular-nums text-[var(--text-primary)]">
          {entry.tracking_number}
        </span>
      )}
      {entry.source === 'arana' && (
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-[var(--mute)]">
          Kentucky
        </span>
      )}
      <span className="flex-1" />
      {entry.expected_delivery && (
        <span
          className="shrink-0 text-[11px] tabular-nums text-[var(--mute)]"
          title={`Expected ${entry.expected_delivery}`}
        >
          ETA {fmtDate(entry.expected_delivery, entry.expected_delivery)}
        </span>
      )}
      {entry.removable && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 p-0 text-[var(--mute)] hover:text-[var(--text-primary)]"
          onClick={handleRemove}
          disabled={remove.isPending}
          aria-label={`Remove ${entry.carrier} ${entry.tracking_number}`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </li>
  )
}

export function TrackingModal() {
  const activeModal = useUIStore((s) => s.activeModal)
  const activePO = useUIStore((s) => s.activePO)
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const open = activeModal === 'tracking'

  const { data: po } = usePO(open ? activePO : null)
  const { data: tracking, isLoading } = usePOTracking(open ? activePO : null)
  const add = useAddPOTracking(activePO)
  const busy = add.isPending

  const form = useForm<TrackingValues>({
    resolver: zodResolver(trackingSchema),
    defaultValues: EMPTY,
  })

  React.useEffect(() => {
    if (open) {
      form.reset(EMPTY)
      add.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function close() {
    setActiveModal(null)
  }

  async function onSubmit(values: TrackingValues) {
    const number = values.tracking_number.trim()
    try {
      const eta = values.expected_delivery.trim()
      await add.mutateAsync({
        carrier: values.carrier,
        tracking_number: number,
        expected_delivery: eta || null,
      })
      toast.success(`${values.carrier} ${number} added`)
      // The modal stays open so the user can see the new entry and add another.
      // The ETA carries over: a split shipment usually lands the same day, and
      // it's easier to clear one field than to re-pick the same date.
      form.reset({
        carrier: values.carrier,
        tracking_number: '',
        expected_delivery: eta,
      })
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 409
          ? `${values.carrier} ${number} is already on this PO`
          : err instanceof Error
            ? err.message
            : 'Failed to add tracking'
      toast.error(msg)
    }
  }

  const rawPoNum = (po?.po_number ?? '').replace(/^#+/, '')
  const poLabel = rawPoNum ? `#${rawPoNum}` : (activePO ?? '')
  const entries = tracking?.entries ?? []

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && close()}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Tracking</DialogTitle>
          <DialogDescription>
            Shipment tracking for PO {poLabel}. Links open on the carrier&apos;s
            site.
          </DialogDescription>
        </DialogHeader>

        <div data-testid="tracking-entries">
          {isLoading ? (
            <p className="py-2 text-xs text-[var(--mute)]">Loading…</p>
          ) : entries.length === 0 ? (
            <p className="py-2 text-xs text-[var(--mute)]">
              No tracking on this PO yet.
            </p>
          ) : (
            <ul className="border-t border-[var(--line-warm)]">
              {entries.map((entry) => (
                <TrackingRow
                  key={
                    entry.tracking_id ?? `arana-${entry.carrier}-${entry.tracking_number}`
                  }
                  entry={entry}
                  poId={activePO}
                />
              ))}
            </ul>
          )}
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-[110px_1fr] gap-3">
              <FormField
                control={form.control}
                name="carrier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Carrier</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={busy}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="tracking-carrier">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CARRIERS.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tracking_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tracking number</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={busy}
                        autoFocus
                        data-testid="tracking-number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="expected_delivery"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Expected delivery{' '}
                    <span className="font-normal text-[var(--mute)]">
                      (optional)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      disabled={busy}
                      data-testid="tracking-expected-delivery"
                    />
                  </FormControl>
                  <p className="text-xs text-[var(--mute)]">
                    Overrides the delivery date estimated when the PO was
                    created. With several shipments, the list shows the latest.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={close}
                disabled={busy}
              >
                Close
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={busy}
                data-testid="tracking-submit"
              >
                {busy ? 'Adding…' : 'Add tracking'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
